import { computed, ref, onMounted, type ComputedRef } from "vue";
import _ from "lodash";
import { useFetch } from "@/helpers/composables/useFetch";
import { useSettingsStore } from "@/store/settings";
import { storeToRefs } from "pinia";
import { comingSoonImages } from "@/helpers/composables/comingSoonImages";
import {
    productUploadImageUrl,
    type ProductUploadImageSize,
} from "@/helpers/productUploadImageUrl";
import { useGenericStore } from "@/store/genericStore";

// import { route } from "ziggy-js";
interface SpecGroup {
    spec_group_id: number;
    spec_group_name: string;
    sort_order: number;
}

interface SpecValue {
    spec_value_id: number;
    spec_value: string;
    spec_group_id: number;
    sort_order: number;
}

interface VariationSpecification {
    value: SpecValue;
    group: SpecGroup;
}

interface ItemVariation {
    item_variation_id: number;
    price: number;
    in_stock: number;
    variation_specifications: VariationSpecification[];
    rothcoItem: RothcoItem;
    primary_image: ItemImage;
    is_active: number;
    eta_date: string;
}

interface ItemSpecification {
    value: SpecValue;
    group: SpecGroup;
}

interface Pricing {
    web_variation_id: number;
    original_price: number;
    price_to_use_with_discount: number;
    map_price: number;
}

interface ItemImage {
    image_filename: string;
}

interface RothcoItem {
    rothco_item_no: string;
}

interface SwatchImages {
    [key: string]: string;
}

import { formatPrice, formatTotalPrice } from "./usePriceFormatting"

export interface Product {
    item_index: number;
    item_name: string;
    title_tag?: string | null;
    item_variations: ItemVariation[];
    prices?: Pricing[];
    item_specifications: ItemSpecification[];
    swatch_images: SwatchImages;
    msds?: string | null;
    productStatuses: {
        [key: string]: {
            name: string,
            discontinued: boolean
        }
    },
    instock_emails: {
        [key: string]: number
    }
}

export function useProductDetails({
    product,
    route,
    prices,
    allowedVariationIds = null,
}: {
    product: Product,
    route: Function,
    prices: ComputedRef<Pricing[]>,
    allowedVariationIds?: Array<number | string> | null,
}) {
    const quantity = ref(1);
    const selectedVariantValues = ref<SpecValue[]>([]);
    const loading = ref(false);
    const productStatuses = ref(product?.productStatuses);
    const categories = computed(() => {
        return product.categories.filter(cat=>!!cat).sort((a, b) => {
            return a?.level - b?.level;
        }).map((category) => category);
    });

    let lastCategory = computed(()=>{
        if(categories.value.length == 0){
            return null;
        }
        return categories.value[categories.value.length - 1];
    })
    const variantGroups = computed(() => {
        // map spec_group_id to spec_group_name
        return product.item_variations.reduce((acc, variant) => {
            for (let spec of variant.variation_specifications) {
                if (!acc[spec.group.spec_group_id]) {
                    acc[spec.group.spec_group_id] = spec.group.spec_group_name;
                }
            }

            return acc;
        }, {});
    });

    const buyStack = computed(() => {
        // group variations of product by their spec group
        const groupVariations = product.item_variations.reduce((acc, item) => {
            item.variation_specifications.forEach((spec) => {
                if (!acc[spec.group.spec_group_name]) {
                    acc[spec.group.spec_group_name] = {
                        spec_group_id: spec.group.spec_group_id,
                        name: spec.group.spec_group_name,
                        values: [],
                        active_value: getActiveVariantForSpecGroup(
                            spec.group.spec_group_id
                        )?.spec_value,
                        sort_order: spec.group.sort_order
                    };
                }
                //if spec value is not in group add it
                if (
                    !acc[spec.group.spec_group_name].values.find(
                        (v) => v.spec_value_id === spec.value.spec_value_id
                    )
                ) {
                    let swatchImage =
                        product.swatch_images[spec.value.spec_value];

                    acc[spec.group.spec_group_name].values.push({
                        ...spec.value,
                        swatch_image: swatchImage,
                        primary_image: item.primary_image
                            ? [item.primary_image.image_filename]
                            : []
                    });
                }

                // if spec value is in group, add primary image if it exists
                if (
                    acc[spec.group.spec_group_name].values.find(
                        (v) => v.spec_value_id === spec.value.spec_value_id
                    ) && item.primary_image
                ) {

                    acc[spec.group.spec_group_name].values
                        .find(
                            (v) => v.spec_value_id === spec.value.spec_value_id
                        )
                        .primary_image.push(item.primary_image.image_filename);
                }

                acc[spec.group.spec_group_name].values = acc[
                    spec.group.spec_group_name
                ].values.sort((a, b) => a.sort_order - b.sort_order);

                if(spec.group.spec_group_name?.includes?.('Size')){
                    const sizeChartLink = product.item_specifications.find(spec=>(spec.group.spec_group_name == 'Size Chart'))?.value?.spec_value ?? null;
                    if (sizeChartLink && typeof DOMParser !== 'undefined') {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(sizeChartLink, 'text/html');
                        const parserError = doc.querySelector('parsererror');
                        const anchor = doc.querySelector('a');
                        acc[spec.group.spec_group_name].size_chart = (!parserError && anchor !== null) ? anchor.getAttribute('href') : null;
                    }
                }
            });
            return acc;
        }, {});

        return groupVariations;
    });

    const nextMissingVariant = computed(() => {
        return Object.keys(buyStack.value).find(
            (val) => !buyStack.value[val].active_value
        );
    });

    const primaryImage = computed(() => {
        if (
            selectedVariant.value !== undefined &&
            selectedVariantValues.value.length == 0
        ) {
            return selectedVariant.value.primary_image?.image_filename;
        }
        let primaryImage;
        for (let value of selectedVariantValues.value) {
            const group = variantGroups.value[value.spec_group_id];
            if (!buyStack.value[group]) {
                continue;
            }
            let variantValue = buyStack.value[group].values.find(
                (v) => v.spec_value_id === value.spec_value_id
            );
            if (variantValue.primary_image.length > 0) {
                let image = variantValue.primary_image[0];
                if (variantValue.primary_image.every((i) => i === image)) {
                    primaryImage = image;
                    break;
                }
            }
        }
        return primaryImage ?? "";
    });

    const selectedVariant = computed(() => {
        //if there is only one variation, return it
        if (product.item_variations.length === 1) {
            return product.item_variations[0];
        }

        if (selectedVariantValues.value.length === 0) {
            return product.item_variations[0];
        }
        // find the variation that matches the selected values
        return (
            product.item_variations.find((variation) => {
                return variation.variation_specifications.length > 0 
                && variation.variation_specifications.every((spec) => {
                    return selectedVariantValues.value.find((selected) => {
                        return (
                            selected.spec_value_id === spec.value.spec_value_id
                        );
                    });
                });
            }) ?? product.item_variations[0]
        );
    });

    const closestVariant = computed(()=>{
        //if there is only one variation, return it
        if (product.item_variations.length === 1) {
            return product.item_variations[0];
        }

        if (selectedVariantValues.value.length === 0) {
            return product.item_variations[0];
        }
        // find the variation that matches the selected values
        return (
            product.item_variations.find((variation) => {
                return selectedVariantValues.value.every((selected) => {
                    return variation.variation_specifications.find((spec) => {
                        return (
                            selected.spec_value_id === spec.value.spec_value_id
                        );
                    });
                });
            }) ?? product.item_variations[0]
        );
    })

    const catalogPage = computed(()=>{
        return closestVariant?.value?.catalog_page_no ?? '';
    })

    const msds = computed(()=>{
        return product.msds ?? '';
    })

    const variationValueAvailablity = computed(() => {
        let specValueAvailableCombos = new Map();
        let specValueGroupIds = new Map();

        // exclude variations that are out-of-stock and discontinued
        // these will be marked as Unavailable
        // when editing a free coupon item, limit to freeItemVariationIds
        const allowedIds = allowedVariationIds?.length
            ? new Set(allowedVariationIds.map(Number))
            : null;
        const variations = product.item_variations?.filter((variation)=>{
            if (allowedIds && !allowedIds.has(Number(variation.item_variation_id))) {
                return false;
            }
            return !(variation.in_stock == 0 && productStatuses.value[variation.item_variation_id]?.discontinued == 1)
        })
        for(let variation of variations){
            for(let varSpec of variation.variation_specifications){
                if(specValueAvailableCombos.has(varSpec.spec_value_id)){
                    specValueAvailableCombos.set(varSpec.spec_value_id, [
                        ...specValueAvailableCombos.get(varSpec.spec_value_id), variation
                    ]);
                }else{
                    specValueAvailableCombos.set(varSpec.spec_value_id, [variation]);
                }
                specValueGroupIds.set(varSpec.spec_value_id, varSpec.spec_group_id);
            }
        }
        specValueAvailableCombos = Object.fromEntries(specValueAvailableCombos);
        specValueGroupIds = Object.fromEntries(specValueGroupIds);

        for(const specValue in specValueAvailableCombos){
            specValueAvailableCombos[specValue] = specValueAvailableCombos[specValue].filter(variation=>{
                let specGroupId = specValueGroupIds[specValue];
                let variantValueIds = selectedVariantValues.value
                    .filter(variantValue=>(variantValue.spec_group_id != specGroupId))
                    .map(variantValue=>variantValue.spec_value_id);
                return variantValueIds.length == 0 || variation.variation_specifications.some(varSpec=>{
                    return variantValueIds.includes(varSpec.spec_value_id);
                })
            })
        }
        return specValueAvailableCombos;
    });

    const addVariationValue = (specValue: SpecValue) => {
        // if spec group already has selected value, remove it. If spec value in array, skip. Else add it
        if (
            selectedVariantValues.value.find(
                (v) => v.spec_group_id === specValue.spec_group_id
            )
        ) {
            selectedVariantValues.value = selectedVariantValues.value.filter(
                (v) => v.spec_group_id !== specValue.spec_group_id
            );
        }
        const selectedVariantValuesClone = JSON.parse(JSON.stringify(selectedVariantValues.value.sort((a, b) => b.sort_order - a.sort_order)));
        // iterate over previously selected variant values, keeping ones that still match with the most recently selected value
        selectedVariantValuesClone.forEach((selectedVariantValueIter)=>{
            const hasMatchWithCurrentSelections = variationValueAvailablity.value[specValue.spec_value_id].some((variant)=>{
                return selectedVariantValues.value.every((selectedVariantValue)=>{
                    return variant.description[selectedVariantValue.specGroup.name] == selectedVariantValue.spec_value
                })
            })
            if(!hasMatchWithCurrentSelections){
                selectedVariantValues.value = selectedVariantValues.value.filter((selectedVariantValue)=>(selectedVariantValue.spec_value_id != selectedVariantValueIter.spec_value_id))
            }
        })
        selectedVariantValues.value.push(specValue);

        defaultItemNumber.value = null;
    };

    function generateProductImages(size: ProductUploadImageSize | null) {
        let images = product.images
            .sort((a, b) => {
                if (a.image_filename === primaryImage.value) return -1; 
                if (b.image_filename === primaryImage.value) return 1;
                return a.sort_order - b.sort_order;
            })
            .filter((image) => {
                let variants = product.item_variations.filter((variation) => {
                    return image.relatedVariationIds.findIndex(relatedId => relatedId == variation.item_variation_id) !== -1;
                });
                if(selectedVariantValues.value.length > 0){
                    return variants.some((variation) => {
                        return selectedVariantValues.value.every((selectedValue)=>{
                            return variation.variation_specifications.some((spec)=>{
                                return spec.value.spec_value_id == selectedValue.spec_value_id;
                            })
                        });
                    })
                }
                return true;
            })
            .map((image) => {
                return {
                    media: image.image_filename
                        ? productUploadImageUrl(image.image_filename, size)
                        : comingSoonImages().large,
                    alt: product.item_name,
                    defaultMediaType: "image"
                };
            });

            // if images doesn't include primary image, add it
            if(!images.find((image) => image.media.includes(primaryImage.value)) && primaryImage.value){
                images.unshift({
                    media: primaryImage.value
                        ? productUploadImageUrl(primaryImage.value, size)
                        : comingSoonImages().large,
                    alt: product.item_name,
                    defaultMediaType: "image"
                });
            }

            return images;
            
    }

    const productImages = computed(() => generateProductImages(null));

    const productLargeImages = computed(() => generateProductImages("large"));
    
    const productPricing = computed(() => {
        // use prices from composable param, else should expect to fetch pricing
        const pricingObj = (prices?.value ?? pricing.value);
        if (closestVariant.value !== undefined) {
            return pricingObj.find(
                (price) =>
                    price.web_variation_id ===
                    closestVariant.value.item_variation_id
            );
        }
        return pricingObj.reduce((acc, price) => {
            return acc < price.price_to_use_with_discount ? acc : price;
        }, Infinity);
    });

    const totalPrice = computed(() => {
        return formatTotalPrice(productPricing.value?.price_to_use_with_discount, (+quantity.value > 0 ? quantity.value : 0));
    });

    const inStock = computed(() => {
        if (selectedVariant.value === undefined) {
            return product.item_variations.some((variation) => {
                return variation.in_stock === 1;
            });
        } else {
            return selectedVariant.value.in_stock === 1;
        }
    });

    const etaInfo = computed(() => {
        if (selectedVariant.value === undefined) {
            return {
                eta_date: null,
                eta_message: "",
                eta_color: 'text-inStockGreen'
            };
        } else {
            return {
                out_of_stock: productStatuses.value[selectedVariant.value.item_variation_id]?.name != 'Coming Soon',
                eta_date: selectedVariant.value?.eta_date,
                eta_message: productStatuses.value[selectedVariant.value.item_variation_id]?.name == 'Coming Soon' ? 'Coming Soon' : 'Out of Stock',
                eta_color: selectedVariant.value?.is_active == 1 ? 'text-inStockGreen' : 'text-rothcoRed'
            };
        }
    });

    const itemComingSoon = computed(()=>{
        return Object.values(productStatuses.value).every((status)=>status.name == 'Coming Soon');
    })

    const instockEmails = ref(product?.instock_emails ?? {});
    const subscribedForInstockEmail = computed(()=>{
        return instockEmails.value?.[selectedVariant.value.item_variation_id] === 0
    })

    const variantIsAvailable = (specValue: SpecValue) => {
        // check if variant is available based on the current selected variants and the spec value being checked against
        let availableForSelectedVariants = false;
        if (selectedVariantValues.value.length > 0) {
            availableForSelectedVariants = selectedVariantValues.value.every(
                (selectedValue) => {
                    return variationValueAvailablity.value[
                        selectedValue.spec_value_id
                    ].some((variation) => {
                        return (
                            variation.in_stock === 1 &&
                            variation.variation_specifications.find((spec) => {
                                return (
                                    spec.value.spec_value_id ===
                                    selectedValue.spec_value_id
                                );
                            })
                        );
                    });
                }
            );
            if (variationValueAvailablity.value[specValue.spec_value_id]) {
                return (
                    variationValueAvailablity.value[
                        specValue.spec_value_id
                    ].some((variation) => {
                        return variation.in_stock === 1;
                    }) && availableForSelectedVariants
                );
            }
            return false;
        }
        // if no selected variants, check if the spec value has any available variations
        else {
            return variationValueAvailablity.value[specValue.spec_value_id]
                ? variationValueAvailablity.value[specValue.spec_value_id].some(
                      (variation) => {
                          return variation.in_stock === 1;
                      }
                  )
                : false;
        }
    };

    const variationSpecs = computed(() => {
        let specs: ItemSpecification[] = [];
        for (let variation of product.item_variations) {
            for (let spec of variation.variation_specifications) {
                if (
                    !specs.find(
                        (s) =>
                            s.value.spec_value_id === spec.value.spec_value_id
                    )
                ) {
                    specs.push(spec);
                }
            }
        }
        return specs.sort((a, b) => a.group.sort_order - b.group.sort_order);
    });

    const getActiveVariantForSpecGroup = (specGroupId) => {
        return (
            selectedVariantValues.value.find(
                (val) => val.spec_group_id == specGroupId
            ) ?? { spec_value: "" }
        );
    };

    const prop65Spec = computed(() => {
        return product.item_specifications.filter(
            (spec) => spec.group.spec_group_name === "Prop 65 Primary Chemical"
        );
    });

    const currentListItems = computed(() => {
        return product.current_list_items ?? [];
    });


    let downloadImagesComputed =  computed(() => {
        if(product.seo_name){
            const { execute: downloadImages, isFetching: isDownloadingImages } = useFetch(route('productDetail.download_images', [product.seo_name]), {
                immediate: false,
                afterFetch: (response) => {
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${product.item_name} Images.zip`;
                    a.click();
                    window.URL.revokeObjectURL(url);
        
                    document.removeChild(a);
                }
            }).get().blob();
            return { downloadImages, isDownloadingImages };
        }else{
            return { downloadImages: () =>{}, isDownloadingImages: ref(false) };
        }
    });

    let market = computed(() => {
        return product?.market?.market_id ?? 0;
    });
    
    let retailLocatorUrl = computed(() => {
        return route('retail.locator.search', {
            market_type: market.value,
        });
    })

    let defaultItemNumber = ref( "");

    let itemNumber = computed(() => { 
         if(defaultItemNumber.value){
            return defaultItemNumber.value;
        } else if(selectedVariantValues.value.length > 0){
            let firstItem = product.item_variations.find((item) => {
                return selectedVariantValues.value.every((selectedValue) => {
                    return item.variation_specifications.some((spec) => {
                        return spec.spec_value_id == selectedValue.spec_value_id;
                    });
                });
            });
            return firstItem?.rothcoItem?.rothco_item_no;
        }else{
            return product.item_variations[0]?.rothcoItem?.rothco_item_no;
        }
    })

    const selectVariationValuesFromItemNumber = (rothcoItemNo: string) => {
        const allVariations = variationsSorted.value.filter((item) => item.rothcoItem.rothco_item_no == rothcoItemNo);
        const firstItem = allVariations?.[0];
        const singleVariationForItemNumber = allVariations.length == 1;

        if(firstItem){
            // get first variation from list of variations
            // if only single variation, select ALL spec values, else only select the 1st spec value
            const specNames = singleVariationForItemNumber ? Object.keys(buyStack.value) : Object.keys(buyStack.value)?.slice(0,1);
            for(const specName of specNames){
                const selectedVariantValueToAdd = buyStack.value?.[specName]?.values?.find(val=>val.spec_value == firstItem.description[specName])
                if(selectedVariantValueToAdd){
                    addVariationValue({
                        ...selectedVariantValueToAdd,
                        specGroup: { 
                            ...selectedVariantValueToAdd,
                            name: specName
                        }
                    });
                }
            }
            defaultItemNumber.value = rothcoItemNo;
        }
    }

    onMounted(()=>{
        const rothcoItemNo = route().params.item;
        selectVariationValuesFromItemNumber(rothcoItemNo);
    })


    const itemVariations = computed(()=>{
        return product.item_variations?.map((variation)=>{
            return {
                ...variation,
                item_name: product.item_name
            };
        })
    })

    const variationsSorted = computed(()=>{
        return _.orderBy(itemVariations.value, ['is_primary', 'sort_order'], ['desc', 'asc']);
    })


    let { execute: getProductPricing, data: pricingData } = useFetch(route('productDetail.pricing', [product.seo_name]), {
        immediate: false,
        beforeFetch(ctx) {
            ctx.options.headers = {
                ...ctx.options.headers,
                'Accept': 'application/json',
            }
            return ctx;
        },
        onFetchError(ctx) {
            return ctx;
        }
    }).post().json();

    let pricing = computed(() => {
        if(pricingData.value && pricingData.value.prices){
            return Object.values(pricingData.value.prices);
        }
        return [];
    });

    const subscribeToInstockEmail = (variationId)=>{
        const settings = useSettingsStore();
        const { setNotificationMessage } = settings;
        const { user } = storeToRefs(settings);
        const buyStackFlyoutDataStore = useGenericStore("buyStackFlyoutDataStore");
        const {state: buyStackFlyoutDataState} = storeToRefs(buyStackFlyoutDataStore);

        useFetch(route("products.instock_email"), {
            afterFetch(ctx){
                buyStackFlyoutDataState.value.instockEmails[variationId] = 0;
                setNotificationMessage({
                    message: `You're all set! We'll notify you via email as soon as this product is in stock.`,
                    type: 'success'
                })
            },
            onFetchError(ctx){
                setNotificationMessage({
                    message: `There was an issue with your request, please try again.`,
                    type: 'error'
                })
            }
        }).post({
            instock_email: {
                email: user.value.email,
                item_variation_id: variationId
            }
        });
    }

    const relevantCoupons = computed(()=>{
        return product.coupons?.map((coupon)=>{
            return {
                ...coupon,
                meta: JSON.parse(coupon.meta)
            }
        })
        .filter((coupon)=>(!coupon.meta?.exclude_items?.includes(itemNumber.value))) ?? []
    })

    return {
        loading,
        buyStack,
        addVariationValue,
        selectedVariant,
        closestVariant,
        catalogPage,
        msds,
        variationValueAvailablity,
        variantIsAvailable,
        quantity,
        productImages,
        productLargeImages,
        productPricing,
        totalPrice,
        inStock,
        etaInfo,
        itemComingSoon,
        selectedVariantValues,
        nextMissingVariant,
        variationSpecs,
        primaryImage,
        prop65Spec,
        formatPrice,
        currentListItems,
        categories,
        lastCategory,
        downloadImages: downloadImagesComputed.value.downloadImages,
        isDownloadingImages: downloadImagesComputed.value.isDownloadingImages,
        retailLocatorUrl,
        itemNumber,
        defaultItemNumber,
        itemVariations,
        getProductPricing,
        pricing,
        d2c_view_only: product.d2c_view_only,
        instockEmails,
        subscribedForInstockEmail,
        subscribeToInstockEmail,
        relevantCoupons,
        selectVariationValuesFromItemNumber
    };
}

export function fetchProductDetails(product_seo_name: string = '', rothco_item_no: string = ''){
    const settings = useSettingsStore();
    const { setQuickViewProduct, setNotificationMessage } = settings;
    const { isLoadingQuickView, quickViewErrorMessage, user } = storeToRefs(settings);
    return useFetch(route('productDetail.quickView', [product_seo_name]), {
        beforeFetch(ctx){
            quickViewErrorMessage.value = "";
            isLoadingQuickView.value = true;
            ctx.options.headers = {
                ...ctx.options.headers,
                'Accept': 'application/json',
            }
            return ctx;
        },
        afterFetch(ctx) {
            if(ctx.data.errors){
                return;
            }else{
                setQuickViewProduct(ctx.data.product, Object.values(ctx.data.prices));
            }
            isLoadingQuickView.value = false;
        },
        onFetchError(ctx){
            quickViewErrorMessage.value = ctx.data?.error ?? "Failed to load product.";
            isLoadingQuickView.value = false;
            setQuickViewProduct(null);
            return ctx;
        }
    }).get().json();
}
